import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import CircularProgress from '@mui/joy/CircularProgress';

import { ReactFlow, addEdge, Controls, Background, useNodesState, useEdgesState, useReactFlow } from '@xyflow/react';
import '@xyflow/react/dist/style.css';

// Modern Components
import ModernFlowHeader from '../components/flow/ModernFlowHeader';
import ModernFlowSidebar from '../components/flow/ModernFlowSidebar';
import ModernNodePanel from '../components/flow/ModernNodePanel';

// Modals
import PromptEditor from './modals/PromptEditor';
import ExamplesEditor from './modals/ExamplesEditor';
import CodeEditor from './modals/CodeEditor';
import TemplateEditor from './modals/TemplateEditor';
import InstructionsEditor from './modals/InstructionsEditor';
import ChatPromptEditor from './modals/ChatPromptEditor';
import LanguageSelectionModal from '../components/modals/LanguageSelectionModal';
import TestRunModal from '../components/modals/TestRunModal';
import AssistantChat from '../components/modern/AssistantChat';

// Nodes
import AINode from '../components/nodes/AINode';
import CodeNode from '../components/nodes/CodeNode';
import InputNode from '../components/nodes/InputNode';
import PhotoNode from '../components/nodes/PhotoNode';
import PdfNode from '../components/nodes/PdfNode';
import OutputNode from '../components/nodes/OutputNode';
import TemplateNode from '../components/nodes/TemplateNode';
import StateHistoryNode from '../components/nodes/StateHistoryNode';

// Services & Hooks
import { useAutosave } from '../hooks/useAutosave';
import { useAuth } from '../contexts/AuthContext';
import { flowService } from '../api/services/flowService';
import { projectService } from '../api/services/projectService';
import { workspaceService } from '../api/services/workspaceService';

// Config & Theme
import { NODE_TYPES, NODE_CONFIG } from '../config/nodeConfig';
import { themeColors, shadows } from '../styles/theme';

const nodeTypes = {
  ai_node: AINode,
  code_node: CodeNode,
  input_node: InputNode,
  photo_node: PhotoNode,
  pdf_node: PdfNode,
  output_node: OutputNode,
  template_node: TemplateNode,
  state_history_node: StateHistoryNode,
};

const initialNodes = [];
const initialEdges = [];

// Helper component to focus on new nodes
function FocusHelper({ nodeToFocus }) {
  const { setCenter } = useReactFlow();

  useEffect(() => {
    if (nodeToFocus) {
      // Focus on the node with smooth animation
      setTimeout(() => {
        setCenter(nodeToFocus.position.x + 110, nodeToFocus.position.y + 50, {
          duration: 500,
          zoom: 1,
        });
      }, 100);
    }
  }, [nodeToFocus, setCenter]);

  return null;
}

// Add edge gap styles
const edgeStyles = `
  .react-flow__edge-path {
    stroke-dasharray: 5000;
    stroke-dashoffset: 14;
    transition: stroke 0.2s ease, stroke-width 0.2s ease;
  }

  .react-flow__edge.selected .react-flow__edge-path {
    stroke: ${themeColors.primary[500]} !important;
    stroke-width: 3 !important;
  }

  .react-flow__edge.selected .react-flow__edge-path {
    filter: drop-shadow(0 0 4px ${themeColors.primary[500]}60);
  }
`;

export default function ModernDashboard() {
  const { user } = useAuth();
  const { id } = useParams();
  const projectId = id;
  const navigate = useNavigate();

  // ReactFlow State
  const [nodes, setNodes, onNodesChangeBase] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Data State
  const [project, setProject] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [generalSettings, setGeneralSettings] = useState(null);
  const [nodeLabelsMap, setNodeLabelsMap] = useState({});

  // UI State
  const [loading, setLoading] = useState(true);
  const [projectLoading, setProjectLoading] = useState(true);
  const [error, setError] = useState(null);

  // Node Selection State
  const [selectedNode, setSelectedNode] = useState(null);
  const [sidebarVisible, setSidebarVisible] = useState(false);

  // Sidebar Width State
  const [leftSidebarWidth, setLeftSidebarWidth] = useState(280);
  const [rightSidebarWidth, setRightSidebarWidth] = useState(280);

  // Node focus state
  const [nodeToFocus, setNodeToFocus] = useState(null);

  // Modal State
  const [isPromptModalOpen, setPromptModalOpen] = useState(false);
  const [isExamplesModalOpen, setExamplesModalOpen] = useState(false);
  const [isCodeEditorOpen, setCodeEditorOpen] = useState(false);
  const [isTemplateEditorOpen, setTemplateEditorOpen] = useState(false);
  const [isInstructionsModalOpen, setInstructionsModalOpen] = useState(false);
  const [isChatPromptModalOpen, setChatPromptModalOpen] = useState(false);
  const [isLanguageModalOpen, setLanguageModalOpen] = useState(false);
  const [isTestRunModalOpen, setTestRunModalOpen] = useState(false);

  // Test Run State
  const [testRunStatus, setTestRunStatus] = useState('idle');
  const testRunAbortRef = useRef(null);

  // Strip test run fields before saving
  const cleanNodesForSave = useMemo(() => {
    return nodes.map((node) => {
      const { testRunStatus: _s, testRunOutput: _o, testRunError: _e, ...cleanData } = node.data;
      if (_s === undefined && _o === undefined && _e === undefined) return node;
      return { ...node, data: cleanData };
    });
  }, [nodes]);

  // Auto-save
  const { saveStatus, lastSaved, forceSave } = useAutosave({
    nodes: cleanNodesForSave,
    edges,
    projectId,
  });

  // Load project and workspace info
  useEffect(() => {
    const loadProjectInfo = async () => {
      setProjectLoading(true);
      if (!projectId) return;

      try {
        const projectData = await projectService.getProject(projectId);
        const generalSettings = await projectService.getGeneralSettings();

        // Fetch workspace info if available
        if (projectData.workspace_id) {
          try {
            const workspaceData = await workspaceService.getWorkspace(projectData.workspace_id);
            setWorkspace(workspaceData);
          } catch (err) {
            console.error('Failed to load workspace:', err);
          }
        }

        setProject(projectData);
        setGeneralSettings(generalSettings);
        setProjectLoading(false);
      } catch (err) {
        console.error('Failed to load project info:', err);
        setProjectLoading(false);
      }
    };

    loadProjectInfo();
  }, [projectId]);

  // Load flow data
  useEffect(() => {
    const loadFlow = async () => {
      try {
        setLoading(true);
        setError(null);

        const flowData = await flowService.getFlow(projectId || '1');

        if (flowData.nodes) {
          const updatedNodes = flowData.nodes.map((node) => {
            if (node.type === 'input_node' || node.type === 'photo_node') {
              return { ...node, deletable: false };
            }
            return node;
          });
          setNodes(updatedNodes);
        }

        if (flowData.edges) {
          setEdges(flowData.edges);
        }
      } catch (err) {
        console.error('Failed to load flow:', err);
        setError('Failed to load flow data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadFlow();
  }, [projectId, setNodes, setEdges]);

  // Update nodes map whenever nodes or edges change
  useEffect(() => {
    updateNodesMap();
  }, [nodes, edges]);

  const updateNodesMap = () => {
    const labelsMap = {};
    nodes.forEach((node) => {
      labelsMap[node.id] = {
        label: node.data.label,
        inputs: [],
        outputs: [],
      };
    });

    labelsMap['JobName'] = {
      label: 'Job Name',
      inputs: [],
      outputs: [],
    };

    for (const edge of edges) {
      labelsMap[edge.source].outputs.push(labelsMap[edge.target].label);
      labelsMap[edge.target].inputs.push(labelsMap[edge.source].label);
    }

    setNodeLabelsMap(labelsMap);
  };

  // Handlers
  const handleBackToProjects = () => {
    if (saveStatus === 'saving') {
      forceSave().then(() => {
        navigate('/projects');
      });
    } else {
      navigate('/projects');
    }
  };

  const onNodeClick = (event, node) => {
    setSelectedNode(node);
    setSidebarVisible(true);
  };

  const closeSidebar = () => {
    setSidebarVisible(false);
    setSelectedNode(null);
  };

  // Wrap onNodesChange to detect node deletion
  const onNodesChange = useCallback((changes) => {
    // Check if the selected node is being removed
    const removedNodes = changes.filter(change => change.type === 'remove');
    if (selectedNode && removedNodes.some(change => change.id === selectedNode.id)) {
      closeSidebar();
    }
    onNodesChangeBase(changes);
  }, [selectedNode, onNodesChangeBase]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({ ...params, markerEnd: { type: 'arrowclosed' } }, eds)),
    [setEdges]
  );

  // Add node functionality
  const addNode = (nodeType) => {
    const config = NODE_CONFIG[nodeType];
    if (!config) return;

    // Find the Transcripts node to position new nodes near it
    const transcriptsNode = nodes.find(
      (node) => node.data.label?.toLowerCase().includes('transcript')
    );

    let position;
    if (transcriptsNode) {
      // Position new nodes to the right of the Transcripts node with some offset
      const offsetX = 350;
      const offsetY = (nodes.length * 120) % 400; // Stagger vertically
      position = {
        x: transcriptsNode.position.x + offsetX,
        y: transcriptsNode.position.y + offsetY,
      };
    } else {
      // Fallback to random position if Transcripts node not found
      position = {
        x: Math.random() * 400 + 100,
        y: Math.random() * 400 + 100,
      };
    }

    const newNodeId = `${nodeType}_${Date.now()}`;
    const newNode = {
      id: newNodeId,
      type: nodeType,
      position,
      selected: true,
      data: {
        label: `${config.label} ${nodes.length + 1}`,
        ...config.defaultData,
      },
    };

    // Deselect all existing nodes and add the new selected node
    setNodes((nds) => [...nds.map(n => ({ ...n, selected: false })), newNode]);
    setNodeToFocus(newNode);
    setSelectedNode(newNode);
    setSidebarVisible(true);
  };

  // Update node
  const updateNode = (updatedNode) => {
    setNodes((nds) =>
      nds.map((node) => (node.id === updatedNode.id ? updatedNode : node))
    );
    setSelectedNode(updatedNode);
  };

  // Handle node name change
  const handleNodeNameChange = (nodeId, newLabel) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, label: newLabel } }
          : node
      )
    );
  };

  // Handle base output toggle (only one node can be the base output)
  const handleBaseOutputToggle = (nodeId, enabled) => {
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        data: {
          ...node.data,
          is_base_output: node.id === nodeId ? enabled : (enabled ? false : node.data.is_base_output),
        },
      }))
    );
    setSelectedNode((prev) => {
      if (prev && prev.id === nodeId) {
        return { ...prev, data: { ...prev.data, is_base_output: enabled } };
      }
      return prev;
    });
  };

  // Handle code save
  const handleCodeSave = (newCode) => {
    if (!selectedNode) return;

    const updatedNode = {
      ...selectedNode,
      data: { ...selectedNode.data, code: newCode },
    };
    updateNode(updatedNode);
  };

  // Handle project settings update
  const handleProjectSettingsUpdate = async (settings) => {
    if (!project) return;

    try {
      const updatedProjectData = {
        default_export_format: project?.default_export_format,
        ...settings,
      };

      await projectService.updateProject(projectId, updatedProjectData);
      setProject({ ...project, ...updatedProjectData });
    } catch (err) {
      console.error('Failed to update project settings:', err);
    }
  };

  // Handle instructions save (closing is handled by TextEditorModal via onClose)
  const handleInstructionsSave = async (newInstructions) => {
    await handleProjectSettingsUpdate({ instructions: newInstructions });
  };

  // Handle chat prompt save (closing is handled by TextEditorModal via onClose)
  const handleChatPromptSave = async (newPrompt) => {
    await handleProjectSettingsUpdate({ bi_agent_context: newPrompt });
  };

  // Handle language selection save
  const handleLanguagesSave = async (selectedLanguageIds) => {
    try {
      await handleProjectSettingsUpdate({ language_ids: selectedLanguageIds });
      // Reload project to get updated data
      const updatedProject = await projectService.getProject(projectId);
      setProject(updatedProject);
      setLanguageModalOpen(false);
    } catch (err) {
      console.error('Failed to save languages:', err);
    }
  };

  // Test Run handlers
  const clearTestRunData = useCallback(() => {
    setNodes((nds) =>
      nds.map((node) => {
        const { testRunStatus: _s, testRunOutput: _o, testRunError: _e, ...cleanData } = node.data;
        if (_s === undefined && _o === undefined && _e === undefined) return node;
        return { ...node, data: cleanData };
      })
    );
    setTestRunStatus('idle');
  }, [setNodes]);

  const handleStopTestRun = useCallback(() => {
    if (testRunAbortRef.current) {
      testRunAbortRef.current.abort();
      testRunAbortRef.current = null;
    }
    clearTestRunData();
  }, [clearTestRunData]);

  const handleStartTestRun = useCallback(async (transcripts, jobName, state = {}, stateHistory = [], photos = []) => {
    setTestRunModalOpen(false);

    // Force save first
    try {
      await forceSave();
    } catch (err) {
      console.error('Failed to save before test run:', err);
    }

    // Set all nodes to pending
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        data: {
          ...node.data,
          testRunStatus: 'pending',
          testRunOutput: undefined,
          testRunError: undefined,
        },
      }))
    );
    setTestRunStatus('running');

    const abortController = flowService.startTestRun(projectId, transcripts, jobName, state, {
      onNodeStart: (payload) => {
        setNodes((nds) =>
          nds.map((node) =>
            node.id === payload.node_id
              ? { ...node, data: { ...node.data, testRunStatus: 'running' } }
              : node
          )
        );
      },
      onNodeComplete: (payload) => {
        setNodes((nds) =>
          nds.map((node) =>
            node.id === payload.node_id
              ? {
                  ...node,
                  data: {
                    ...node.data,
                    testRunStatus: 'completed',
                    testRunOutput: payload.output,
                  },
                }
              : node
          )
        );
      },
      onNodeError: (payload) => {
        setNodes((nds) =>
          nds.map((node) =>
            node.id === payload.node_id
              ? {
                  ...node,
                  data: {
                    ...node.data,
                    testRunStatus: 'error',
                    testRunError: payload.error,
                  },
                }
              : node
          )
        );
      },
      onDone: () => {
        setTestRunStatus('completed');
        testRunAbortRef.current = null;
      },
      onError: (payload) => {
        setTestRunStatus('error');
        testRunAbortRef.current = null;
        console.error('Test run error:', payload);
      },
    }, stateHistory, photos);

    testRunAbortRef.current = abortController;
  }, [projectId, forceSave, setNodes]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (testRunAbortRef.current) {
        testRunAbortRef.current.abort();
      }
    };
  }, []);

  // Sidebar resize handlers
  const handleLeftResize = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = leftSidebarWidth;

    const handleMouseMove = (e) => {
      const diff = e.clientX - startX;
      const newWidth = Math.min(Math.max(startWidth + diff, 240), 480);
      setLeftSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'default';
      document.body.style.userSelect = 'auto';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
  }, [leftSidebarWidth]);

  const handleRightResize = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = rightSidebarWidth;

    const handleMouseMove = (e) => {
      const diff = startX - e.clientX;
      const newWidth = Math.min(Math.max(startWidth + diff, 240), 400);
      setRightSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'default';
      document.body.style.userSelect = 'auto';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
  }, [rightSidebarWidth]);

  return (
    <>
      <style>{edgeStyles}</style>
      <Sheet
        sx={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          backgroundColor: themeColors.neutral[25],
        }}
      >
      {/* Header */}
      <ModernFlowHeader
        project={project}
        workspace={workspace}
        onBack={handleBackToProjects}
        saveStatus={saveStatus}
        lastSaved={lastSaved}
        onTestRunClick={() => setTestRunModalOpen(true)}
        testRunStatus={testRunStatus}
        onStopTestRun={handleStopTestRun}
        onClearTestRun={clearTestRunData}
      />

      {/* Main Content Area */}
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          overflow: 'hidden',
        }}
      >
        {/* Left Sidebar */}
        {!projectLoading ? (
          <ModernFlowSidebar
            onAddNode={addNode}
            project={project}
            onUpdateProject={handleProjectSettingsUpdate}
            supportedLanguages={generalSettings?.transcript_languages || []}
            selectedLanguages={project?.supported_languages || []}
            onLanguagesChange={() => setLanguageModalOpen(true)}
            onInstructionsClick={() => setInstructionsModalOpen(true)}
            onChatPromptClick={() => setChatPromptModalOpen(true)}
            width={leftSidebarWidth}
            onResize={handleLeftResize}
            nodes={nodes}
          />
        ) : (
          <Box
            sx={{
              width: 320,
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: themeColors.neutral[25],
              borderRight: `1px solid ${themeColors.neutral[100]}`,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* ReactFlow Canvas */}
        <Box
          sx={{
            flex: 1,
            height: '100%',
            position: 'relative',
            backgroundColor: '#fafafa',
          }}
        >
          {error ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Typography level="h4" sx={{ color: themeColors.danger, mb: 2 }}>
                Error
              </Typography>
              <Typography level="body-md" sx={{ mb: 4, color: themeColors.neutral[700] }}>
                {error}
              </Typography>
              <Button
                onClick={() => window.location.reload()}
                sx={{
                  background: `linear-gradient(135deg, ${themeColors.primary[500]}, ${themeColors.primary[600]})`,
                  boxShadow: shadows.md,
                }}
              >
                Retry
              </Button>
            </Box>
          ) : (
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onNodeClick={onNodeClick}
              onConnect={onConnect}
              nodeTypes={nodeTypes}
              fitView
              style={{ width: '100%', height: '100%' }}
              defaultEdgeOptions={{
                style: { strokeWidth: 2, stroke: themeColors.neutral[300] },
                markerEnd: {
                  type: 'arrowclosed',
                  color: themeColors.neutral[300],
                  markerUnits: 'strokeWidth',
                  orient: 'auto-start-reverse',
                },
              }}
            >
              <Controls
                style={{
                  button: {
                    backgroundColor: 'white',
                    border: 'none',
                    boxShadow: shadows.md,
                    borderRadius: 8,
                  },
                }}
              />
              <Background
                color="#c8c8c8"
                gap={20}
                size={2}
              />
              <FocusHelper nodeToFocus={nodeToFocus} />
            </ReactFlow>
          )}
        </Box>

        {/* Right Panel - Node Properties */}
        <ModernNodePanel
          visible={sidebarVisible}
          selectedNode={selectedNode}
          onClose={closeSidebar}
          onNodeNameChange={handleNodeNameChange}
          onOpenPromptEditor={() => setPromptModalOpen(true)}
          onOpenCodeEditor={() => setCodeEditorOpen(true)}
          onOpenTemplateEditor={() => setTemplateEditorOpen(true)}
          onOpenExamplesEditor={() => setExamplesModalOpen(true)}
          onNodeUpdate={updateNode}
          onBaseOutputToggle={handleBaseOutputToggle}
          nodeInputs={nodeLabelsMap[selectedNode?.id]?.inputs || []}
          nodeOutputs={nodeLabelsMap[selectedNode?.id]?.outputs || []}
          width={rightSidebarWidth}
          onResize={handleRightResize}
        />
      </Box>

      {/* Modals */}
      <PromptEditor
        open={isPromptModalOpen}
        onClose={() => setPromptModalOpen(false)}
        selectedNode={selectedNode}
        availableVariables={[...(nodeLabelsMap[selectedNode?.id]?.inputs || []), 'JobName']}
        onSave={(newPrompt) => {
          if (!selectedNode) return;
          const updatedNode = {
            ...selectedNode,
            data: { ...selectedNode.data, prompt: newPrompt },
          };
          updateNode(updatedNode);
        }}
      />

      <ExamplesEditor
        open={isExamplesModalOpen}
        onClose={() => setExamplesModalOpen(false)}
        selectedNode={selectedNode}
        onUpdateExamples={(newExamples) => {
          if (!selectedNode) return;
          const updatedNode = {
            ...selectedNode,
            data: { ...selectedNode.data, examples: newExamples },
          };
          updateNode(updatedNode);
        }}
      />

      <CodeEditor
        open={isCodeEditorOpen}
        onClose={() => setCodeEditorOpen(false)}
        selectedNode={selectedNode}
        onSave={handleCodeSave}
      />

      <TemplateEditor
        open={isTemplateEditorOpen}
        onClose={() => setTemplateEditorOpen(false)}
        selectedNode={selectedNode}
        availableVariables={[...(nodeLabelsMap[selectedNode?.id]?.inputs || []), 'JobName']}
        onSave={(newTemplate) => {
          if (!selectedNode) return;
          const updatedNode = {
            ...selectedNode,
            data: { ...selectedNode.data, template: newTemplate },
          };
          updateNode(updatedNode);
        }}
      />

      {/* Instructions Editor */}
      <InstructionsEditor
        open={isInstructionsModalOpen}
        onClose={() => setInstructionsModalOpen(false)}
        value={project?.instructions || ''}
        onSave={handleInstructionsSave}
      />

      {/* Chat Prompt Editor */}
      <ChatPromptEditor
        open={isChatPromptModalOpen}
        onClose={() => setChatPromptModalOpen(false)}
        value={project?.bi_agent_context || ''}
        onSave={handleChatPromptSave}
      />

      {/* Language Selection Modal */}
      <LanguageSelectionModal
        open={isLanguageModalOpen}
        onClose={() => setLanguageModalOpen(false)}
        supportedLanguages={generalSettings?.transcript_languages || []}
        selectedLanguages={project?.supported_languages || []}
        onSave={handleLanguagesSave}
      />

      {/* Test Run Modal */}
      <TestRunModal
        open={isTestRunModalOpen}
        onClose={() => setTestRunModalOpen(false)}
        onStartTestRun={handleStartTestRun}
        isRunning={testRunStatus === 'running'}
        isStateful={project?.mode === 'stateful_job'}
      />

      <AssistantChat context={{ project_id: projectId, project_name: project?.name }} />
    </Sheet>
    </>
  );
}
